import { useState } from "react";
import { useFavorites, type FavoriteItem } from "@/hooks/useLearning";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

type FavType = "tumu" | FavoriteItem["type"];

const typeTabs: { key: FavType; label: string; icon: string }[] = [
  { key: "tumu", label: "Tümü", icon: "ri-apps-line" },
  { key: "video", label: "Videolar", icon: "ri-play-circle-line" },
  { key: "soru", label: "Sorular", icon: "ri-list-check-2" },
  { key: "konu", label: "Konular", icon: "ri-book-2-line" },
];

const typeConfig: Record<FavoriteItem["type"], { label: string; badge: string }> = {
  video: { label: "Video", badge: "bg-primary-100 text-primary-700" },
  soru: { label: "Soru", badge: "bg-accent-100 text-accent-700" },
  konu: { label: "Konu", badge: "bg-secondary-100 text-secondary-800" },
};

export default function Favorilerim() {
  const { data, loading, error, reload, removeFavorite } = useFavorites();
  const [tab, setTab] = useState<FavType>("tumu");

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const items = data ?? [];
  const filtered = items.filter((f) => (tab === "tumu" ? true : f.type === tab));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Favorilerim</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Beğendiğin video, soru ve konular burada.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold whitespace-nowrap">
          <i className="ri-heart-line" aria-hidden="true"></i>
          {filtered.length} favori
        </span>
      </div>

      <div className="inline-flex rounded-full bg-background-200/70 p-1">
        {typeTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.key
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className={t.icon} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="bg-background-50 rounded-xl border border-background-200 px-5 py-4 flex items-center gap-4"
          >
            <span className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${typeConfig[item.type].badge}`}>
              <i
                className={
                  item.type === "video"
                    ? "ri-play-circle-line"
                    : item.type === "soru"
                    ? "ri-list-check-2"
                    : "ri-book-2-line"
                }
                aria-hidden="true"
              ></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground-900 truncate">{item.title}</p>
              <p className="text-xs text-foreground-500 mt-0.5">{item.subject} · {item.meta}</p>
            </div>
            <button
              type="button"
              onClick={() => removeFavorite(item.id)}
              className="w-8 h-8 flex items-center justify-center rounded-md text-accent-600 hover:bg-accent-50 transition-colors"
            >
              <i className="ri-heart-fill text-lg" aria-hidden="true"></i>
            </button>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <p className="text-sm text-foreground-500">Bu kategoride favori yok.</p>
          </div>
        )}
      </div>
    </div>
  );
}